import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Search, Mail, Calendar, Clock, ChevronRight, Sparkles, TrendingUp, Bot, BarChart3, Link2, Zap, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// Lazily fetches a single post's featured_image when the card scrolls into view.
// Avoids loading dozens of multi-MB base64 data URLs up front.
const lazyImageCache = new Map<string, string | null>();
const LazyDynamicImage = ({ slug, alt, accent, fallback }: { slug: string; alt: string; accent: string; fallback: React.ReactNode }) => {
  const [src, setSrc] = useState<string | null>(() => lazyImageCache.get(slug) ?? null);
  const [tried, setTried] = useState<boolean>(() => lazyImageCache.has(slug));
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (tried || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          setTried(true);
          (async () => {
            const { data } = await supabase
              .from("blog_posts")
              .select("featured_image")
              .eq("client_id", ORBITRANK_CLIENT_ID)
              .eq("slug", slug)
              .maybeSingle();
            const url = (data?.featured_image as string | null) || null;
            lazyImageCache.set(slug, url);
            if (url) setSrc(url);
          })();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [slug, tried]);

  if (src) {
    return (
      <>
        <img src={src} alt={alt} loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(3,5,10,0) 40%, rgba(3,5,10,0.85) 100%), radial-gradient(circle at 50% 30%, ${accent}10, transparent 70%)` }} />
      </>
    );
  }
  return <div ref={ref} className="absolute inset-0">{fallback}</div>;
};


const ORBITRANK_CLIENT_ID = "1ff7ac35-bc13-4a46-9290-59cd4def086d";
import logoFull from "@/assets/orbitrank-logo-v3.png";
import logoMark from "@/assets/orbitrank-mark.png";
import "@/styles/landing.css";
import imgAutomatedSeo from "@/assets/blog/automated-seo.jpg";
import imgAiCitation from "@/assets/blog/ai-citation.jpg";
import imgSeoAutomation from "@/assets/blog/seo-automation.jpg";
import imgAiRanking from "@/assets/blog/ai-ranking.jpg";

const SLUG_IMAGES: Record<string, string> = {
  "automated-seo-rank-more-do-less": imgAutomatedSeo,
  "getting-cited-by-ai": imgAiCitation,
  "seo-automation-explained": imgSeoAutomation,
  "ai-search-ranking-factors": imgAiRanking,
};

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Features", href: "/#features" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Resources", href: "#", caret: true },
  { label: "Blog", href: "/blog", active: true },
];

const CATEGORIES = [
  "All Posts", "AI SEO", "SEO Automation", "Rankings", "AI Search", "Case Studies", "Product Updates", "Guides",
];

type Post = {
  category: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  slug?: string;
  image?: string;
};

const ACCENTS = ["#00FF88", "#A78BFA", "#FB923C", "#38BDF8"];
const formatDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const POSTS: Post[] = [
  { category: "AI SEO", title: "How AI Search is Changing the Game (And How to Win)", excerpt: "AI search engines are rewriting the rules. Here's how to make sure your brand shows up where it matters.", date: "May 16, 2026", readTime: "8 min read", icon: Sparkles, accent: "#00FF88" },
  { category: "SEO Automation", title: "The Power of Automated SEO: Rank More, Do Less", excerpt: "Discover how automation handles the heavy lifting — so you can focus on growth, not grunt work.", date: "May 14, 2026", readTime: "10 min read", icon: TrendingUp, accent: "#A78BFA", slug: "automated-seo-rank-more-do-less" },
  { category: "Case Study", title: "From 0 to 10K Visitors: An AI SEO Case Study", excerpt: "See how one business used OrbitRank to grow organic traffic 10x in just 60 days — without hiring an agency.", date: "May 11, 2026", readTime: "7 min read", icon: BarChart3, accent: "#FB923C" },
  { category: "AI Search", title: "Getting Cited by AI: The New SEO Frontier", excerpt: "Learn how to optimize your content to get cited by ChatGPT, Gemini, and other AI engines.", date: "May 9, 2026", readTime: "6 min read", icon: Bot, accent: "#38BDF8", slug: "getting-cited-by-ai" },
  { category: "Rankings", title: "5 Ranking Factors That Still Matter in 2026", excerpt: "Google evolves, but these core ranking factors remain crucial for long-term SEO success.", date: "May 7, 2026", readTime: "9 min read", icon: Link2, accent: "#00FF88" },
  { category: "Product Update", title: "Introducing: AI Content Optimizer 2.0", excerpt: "Smarter recommendations, better keywords, and deeper SERP analysis — now live in OrbitRank.", date: "May 5, 2026", readTime: "4 min read", icon: Zap, accent: "#A78BFA" },
  { category: "Guides", title: "AI Visibility for Local Businesses: The Playbook", excerpt: "A step-by-step guide to making your local business discoverable across AI-powered search.", date: "May 2, 2026", readTime: "12 min read", icon: Globe, accent: "#FB923C" },
  { category: "SEO Automation", title: "SEO Automation Explained: What Runs, What Doesn't", excerpt: "A clear breakdown of the parts of SEO that can run on autopilot — and the parts that still need a human.", date: "Apr 28, 2026", readTime: "8 min read", icon: TrendingUp, accent: "#38BDF8", slug: "seo-automation-explained" },
  { category: "AI Search", title: "AI Search Ranking Factors You Can't Ignore", excerpt: "What ChatGPT, Perplexity, and Gemini actually look at when deciding which brands to surface.", date: "Apr 24, 2026", readTime: "7 min read", icon: Sparkles, accent: "#00FF88", slug: "ai-search-ranking-factors" },
];

const Blog = () => {
  const navigate = useNavigate();
  const [activeCat, setActiveCat] = useState("All Posts");
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(6);

  const [dynamicPosts, setDynamicPosts] = useState<Post[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // IMPORTANT: do NOT select `content` or `featured_image` here — new
      // AI images can be multi-MB data URLs. The list view only needs text
      // metadata; pulling images made new posts appear several seconds late.
      const { data } = await supabase
        .from("blog_posts")
        .select("title,slug,meta_description,published_at,created_at,keyword")
        .eq("client_id", ORBITRANK_CLIENT_ID)
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(60);
      if (cancelled || !data) return;
      const mapped: Post[] = data
        .filter((d) => d.slug && d.title)
        .map((d, i) => ({
          category: (d.keyword && typeof d.keyword === "string" && !d.keyword.startsWith("{")) ? d.keyword : "AI SEO",
          title: d.title as string,
          excerpt: (d.meta_description || "").slice(0, 200),
          date: formatDate((d.published_at || d.created_at) as string),
          readTime: "5 min read",
          icon: Sparkles,
          accent: ACCENTS[i % ACCENTS.length],
          slug: d.slug as string,
        }));
      setDynamicPosts(mapped);
    })();
    return () => { cancelled = true; };
  }, []);

  const allPosts = [...dynamicPosts, ...POSTS];
  const filtered = allPosts.filter((p) => {
    const catOk = activeCat === "All Posts" || p.category.toLowerCase().includes(activeCat.toLowerCase()) || activeCat.toLowerCase().includes(p.category.toLowerCase());
    const q = query.trim().toLowerCase();
    const qOk = !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q);
    return catOk && qOk;
  });

  return (
    <div className="min-h-screen bg-[#03050A] text-white overflow-x-hidden w-full max-w-[100vw]" style={{ fontFamily: "Inter, ui-sans-serif, system-ui" }}>
      {/* glow top line */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(0,255,136,0.4), transparent)" }} />

      {/* NAVBAR */}
      <header className="relative z-20 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16 md:h-20 gap-3">
        <a href="/" className="flex items-center min-w-0 flex-shrink">
          <img src={logoFull} alt="OrbitRank" className="h-7 md:h-8 w-auto object-contain" />
        </a>
        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <div key={l.label} className="relative">
              <a
                href={l.href}
                onClick={(e) => {
                  if (l.caret) {
                    e.preventDefault();
                    setResourcesOpen(!resourcesOpen);
                  }
                }}
                className={`text-[14px] font-medium transition-colors flex items-center gap-1 cursor-pointer ${l.active ? "text-[#00FF88]" : "text-white/85 hover:text-white"}`}
                style={l.active ? { textShadow: "0 0 12px rgba(0,255,136,0.55)" } : undefined}
              >
                {l.label}
                {l.caret && <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform ${resourcesOpen ? "rotate-180" : ""}`} />}
              </a>
              {l.caret && resourcesOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 py-2 rounded-xl bg-[#0a0d14] border border-white/10 shadow-2xl z-50">
                  {[ { label: "Guides", href: "#" }, { label: "Case Studies", href: "#" }, { label: "Help Center", href: "#" } ].map((item) => (
                    <a key={item.label} href={item.href} className="block px-4 py-2 text-[13px] text-white/70 hover:text-white hover:bg-white/5 transition-colors">
                      {item.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          <button onClick={() => navigate("/auth")} className="hidden md:inline-flex text-[14px] font-medium text-white/85 hover:text-white">Log in</button>
          <button
            onClick={() => navigate("/activate")}
            className="inline-flex items-center gap-1.5 md:gap-2 h-9 md:h-10 px-2.5 md:px-5 rounded-lg text-[11px] md:text-[14px] font-semibold text-[#04060A] bg-[#00FF88] hover:bg-[#1aff96] transition-all whitespace-nowrap leading-none flex-shrink-0"
            style={{ boxShadow: "0 0 0 1px rgba(0,255,136,0.4), 0 8px 24px rgba(0,255,136,0.25)" }}
          >
            <span className="whitespace-nowrap">Launch My SEO</span>
            <ArrowRight className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 md:pt-10 pb-12 md:pb-20">


        <div className="relative grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* LEFT */}
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase"
              style={{ background: "rgba(0,255,136,0.1)", color: "#00FF88", border: "1px solid rgba(0,255,136,0.3)", boxShadow: "0 0 20px rgba(0,255,136,0.15)" }}>
              Blog
            </span>
            <h1 className="mt-4 md:mt-5 text-[32px] sm:text-[40px] md:text-[56px] leading-[1.1] md:leading-[1.05] font-bold tracking-tight">
              AI SEO insights<br />
              that <span style={{ color: "#00FF88", textShadow: "0 0 30px rgba(0,255,136,0.45)" }}>move the needle.</span>
            </h1>
            <p className="mt-4 md:mt-6 text-[14px] md:text-[17px] text-white/65 max-w-[520px] leading-relaxed">
              Actionable strategies, real case studies, and platform updates to help you grow rankings, traffic, and visibility — automatically.
            </p>

            <form onSubmit={(e) => e.preventDefault()} className="hidden md:flex mt-6 md:mt-8 items-center gap-2 p-1.5 rounded-xl w-full max-w-[480px] min-w-0"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(10px)" }}>
              <input type="email" placeholder="Enter your email"
                className="flex-1 min-w-0 bg-transparent text-[13px] md:text-[14px] text-white placeholder:text-white/40 px-2 md:px-3 py-2 outline-none" />
              <button type="submit"
                className="px-3 md:px-5 py-2 md:py-2.5 rounded-lg text-[12px] md:text-[13px] font-semibold text-[#04060A] bg-[#00FF88] hover:bg-[#1aff96] transition-all whitespace-nowrap flex-shrink-0"
                style={{ boxShadow: "0 0 20px rgba(0,255,136,0.35)" }}>
                Subscribe
              </button>
            </form>
            <p className="hidden md:block mt-3 text-[12px] text-white/40">No spam. Unsubscribe anytime.</p>
          </div>

          {/* RIGHT — dashboard visual */}
          <div className="relative w-full min-w-0 hidden md:block">
            <div className="relative rounded-2xl p-4 md:p-6 overflow-hidden w-full"
              style={{
                background: "linear-gradient(160deg, rgba(15,20,30,0.95), rgba(8,12,20,0.95))",
                border: "1px solid rgba(0,255,136,0.18)",
                boxShadow: "0 30px 80px -20px rgba(0,255,136,0.25), 0 0 0 1px rgba(255,255,255,0.04) inset",
              }}>
              {/* header */}
              <div className="flex items-center gap-2 mb-4 md:mb-5 min-w-0">
                <img src={logoMark} alt="" className="w-5 h-5 md:w-6 md:h-6 object-contain flex-shrink-0" />
                <span className="text-[12px] md:text-[13px] font-semibold"><span className="text-white">Orbit</span><span style={{ color: "#00FF88" }}>Rank</span></span>
                <span className="ml-auto text-[10px] md:text-[11px] text-white/40 truncate">OrbitRank Blog</span>
              </div>

              {/* chart */}
              <div className="relative h-[160px] md:h-[220px] rounded-lg overflow-hidden"
                style={{ background: "radial-gradient(circle at 30% 70%, rgba(0,255,136,0.08), transparent 60%)" }}>
                <svg viewBox="0 0 400 220" className="w-full h-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="blogChartFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#00FF88" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#00FF88" stopOpacity="0" />
                    </linearGradient>
                    <filter id="blogGlow">
                      <feGaussianBlur stdDeviation="3" result="b" />
                      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>
                  {/* grid */}
                  {[40, 90, 140, 190].map((y) => (
                    <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="rgba(255,255,255,0.04)" />
                  ))}
                  <path d="M0,180 L40,165 L80,170 L120,140 L160,150 L200,110 L240,120 L280,80 L320,90 L360,50 L400,30 L400,220 L0,220 Z" fill="url(#blogChartFill)" />
                  <path d="M0,180 L40,165 L80,170 L120,140 L160,150 L200,110 L240,120 L280,80 L320,90 L360,50 L400,30"
                    stroke="#00FF88" strokeWidth="2.5" fill="none" filter="url(#blogGlow)" />
                  <circle cx="400" cy="30" r="5" fill="#00FF88" filter="url(#blogGlow)">
                    <animate attributeName="r" values="4;6;4" dur="2.4s" repeatCount="indefinite" />
                  </circle>
                </svg>

                {/* floating tags */}
                <span className="absolute top-2 left-2 md:top-3 md:left-3 px-1.5 md:px-2 py-0.5 md:py-1 rounded-md text-[9px] md:text-[10px] font-medium"
                  style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(0,255,136,0.3)", color: "#00FF88" }}>AI Search</span>
                <span className="absolute bottom-2 left-2 md:bottom-3 md:left-3 px-1.5 md:px-2 py-0.5 md:py-1 rounded-md text-[9px] md:text-[10px] font-medium"
                  style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(167,139,250,0.3)", color: "#A78BFA" }}>SEO Auto</span>
                <span className="absolute bottom-2 right-2 md:bottom-12 md:right-3 px-1.5 md:px-2 py-0.5 md:py-1 rounded-md text-[9px] md:text-[10px] font-medium"
                  style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(56,189,248,0.3)", color: "#38BDF8" }}>Visibility</span>
              </div>

              {/* mini stats */}
              <div className="grid grid-cols-3 gap-2 md:gap-3 mt-4 md:mt-5">
                {[
                  { l: "Traffic", v: "+182%", c: "#00FF88" },
                  { l: "Rankings", v: "Top 3", c: "#A78BFA" },
                  { l: "Citations", v: "1.2k", c: "#FB923C" },
                ].map((s) => (
                  <div key={s.l} className="rounded-lg p-2 md:p-3 min-w-0" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="text-[9px] md:text-[10px] text-white/50 uppercase tracking-wider truncate">{s.l}</div>
                    <div className="text-[13px] md:text-[16px] font-bold mt-1 truncate" style={{ color: s.c }}>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY BAR */}
      <section className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pb-8 md:pb-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
          <div className="flex items-center gap-1.5 md:gap-2 flex-wrap -mx-1 md:mx-0">
            {CATEGORIES.map((c) => {
              const active = c === activeCat;
              return (
                <button key={c} onClick={() => setActiveCat(c)}
                  className="px-3 md:px-4 py-1.5 md:py-2 rounded-full text-[12px] md:text-[13px] font-medium transition-all whitespace-nowrap"
                  style={active
                    ? { background: "rgba(0,255,136,0.12)", color: "#00FF88", border: "1px solid rgba(0,255,136,0.4)", boxShadow: "0 0 20px rgba(0,255,136,0.2)" }
                    : { background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  {c}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-full w-full md:min-w-[240px] md:w-auto"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Search className="w-4 h-4 text-white/40 flex-shrink-0" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search articles…"
              className="flex-1 min-w-0 bg-transparent text-[13px] text-white placeholder:text-white/40 outline-none" />
          </div>
        </div>
      </section>

      {/* BLOG GRID */}
      <section className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pb-12 md:pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.slice(0, visible).map((p, i) => {
            const Icon = p.icon;
            return (
              <article key={i} className="group relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                style={{
                  background: "linear-gradient(160deg, rgba(15,20,30,0.85), rgba(8,12,20,0.85))",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
                onClick={() => { if (p.slug) navigate(`/blog/${p.slug}`); }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = `0 20px 60px -20px ${p.accent}55, 0 0 0 1px ${p.accent}40`; (e.currentTarget as HTMLElement).style.borderColor = `${p.accent}55`; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = "none"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.06)"; }}
              >
                {/* visual */}
                <div className="relative h-[140px] md:h-[200px] overflow-hidden"
                  style={{ background: `radial-gradient(circle at 50% 50%, ${p.accent}25, transparent 70%), linear-gradient(135deg, rgba(15,20,30,1), rgba(5,8,14,1))` }}>
                  {(() => {
                    const staticImg = p.image || (p.slug && SLUG_IMAGES[p.slug]);
                    const iconFallback = (
                      <>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="relative">
                            <div className="absolute inset-0 rounded-full" style={{ background: p.accent, opacity: 0.25, filter: "blur(30px)", transform: "scale(1.6)" }} />
                            <div className="relative w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110"
                              style={{ background: "rgba(0,0,0,0.5)", border: `1px solid ${p.accent}66`, boxShadow: `0 0 30px ${p.accent}55` }}>
                              <Icon className="w-5 h-5 md:w-7 md:h-7" />
                            </div>
                          </div>
                        </div>
                        <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 400 180" preserveAspectRatio="none">
                          {[40, 80, 120, 160].map((y) => <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="rgba(255,255,255,0.05)" />)}
                          {[80, 160, 240, 320].map((x) => <line key={x} y1="0" y2="180" x1={x} x2={x} stroke="rgba(255,255,255,0.05)" />)}
                        </svg>
                      </>
                    );
                    if (staticImg) {
                      return (
                        <>
                          <img src={staticImg} alt={p.title} loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(3,5,10,0) 40%, rgba(3,5,10,0.85) 100%), radial-gradient(circle at 50% 30%, ${p.accent}10, transparent 70%)` }} />
                        </>
                      );
                    }
                    if (p.slug) {
                      return <LazyDynamicImage slug={p.slug} alt={p.title} accent={p.accent} fallback={iconFallback} />;
                    }
                    return iconFallback;
                  })()}
                </div>


                {/* content */}
                <div className="p-4 md:p-6">
                  <span className="inline-block px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
                    style={{ background: `${p.accent}15`, color: p.accent, border: `1px solid ${p.accent}40` }}>
                    {p.category}
                  </span>
                  <h3 className="mt-3 md:mt-4 text-[15px] md:text-[18px] font-semibold leading-snug text-white group-hover:text-white">
                    {p.title}
                  </h3>
                  <p className="mt-2 md:mt-3 text-[13px] md:text-[14px] text-white/55 leading-relaxed line-clamp-2 md:line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-4 md:mt-5 flex items-center justify-between text-[11px] md:text-[12px] text-white/45 gap-2">
                    <div className="flex items-center gap-2 md:gap-3 min-w-0">
                      <span className="flex items-center gap-1.5 truncate"><Calendar className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0" />{p.date}</span>
                      <span className="opacity-50">•</span>
                      <span className="flex items-center gap-1.5 whitespace-nowrap"><Clock className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0" />{p.readTime}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" style={{ color: p.accent }} />
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-white/50">No articles match your search.</div>
        )}

        {/* LOAD MORE */}
        {visible < filtered.length && (
          <div className="mt-12 flex justify-center">
            <button onClick={() => setVisible((v) => v + 6)}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-lg text-[13px] font-semibold transition-all"
              style={{ background: "rgba(0,255,136,0.08)", color: "#00FF88", border: "1px solid rgba(0,255,136,0.35)", boxShadow: "0 0 20px rgba(0,255,136,0.15)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = "0 0 35px rgba(0,255,136,0.35)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = "0 0 20px rgba(0,255,136,0.15)"; }}>
              Load more articles <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>

      {/* NEWSLETTER */}
      <section className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pb-16 md:pb-20">
        <div className="relative rounded-2xl p-5 sm:p-8 md:p-10 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(15,20,30,0.95), rgba(8,12,20,0.95))",
            border: "1px solid rgba(255,255,255,0.08)",
          }}>
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-8">
            <div className="flex items-center gap-4 md:gap-5 max-w-[500px]">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(0,255,136,0.1)", border: "1px solid rgba(0,255,136,0.3)", boxShadow: "0 0 25px rgba(0,255,136,0.25)" }}>
                <Mail className="w-5 h-5 md:w-6 md:h-6" style={{ color: "#00FF88" }} />
              </div>
              <div>
                <h3 className="text-[17px] md:text-[22px] font-bold leading-tight">Get the latest AI SEO insights delivered to your inbox.</h3>
              </div>
            </div>
            <form onSubmit={(e) => e.preventDefault()} className="flex items-center gap-2 p-1.5 rounded-xl w-full md:flex-1 md:max-w-[480px] min-w-0"
              style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <input type="email" placeholder="Enter your email"
                className="flex-1 min-w-0 bg-transparent text-[14px] text-white placeholder:text-white/40 px-3 py-2 outline-none" />
              <button type="submit" className="px-4 md:px-5 py-2.5 rounded-lg text-[13px] font-semibold text-[#04060A] bg-[#00FF88] hover:bg-[#1aff96] transition-all whitespace-nowrap flex-shrink-0"
                style={{ boxShadow: "0 0 20px rgba(0,255,136,0.35)" }}>
                Subscribe
              </button>
            </form>
          </div>
          <p className="relative mt-4 text-[12px] text-white/40 text-center md:text-right">No spam. Unsubscribe anytime.</p>
        </div>
      </section>

      {/* FOOTER (reused style) */}
      <footer className="lo-footer">
        <div className="lo-footer-top">
          <div className="lo-footer-brand">
            <div className="lo-footer-logo" style={{ gap: 2 }}>
              <img src={logoMark} alt="OrbitRank logo" style={{ width: 36, height: 36, objectFit: "contain", background: "transparent" }} />
              <span><span style={{ color: "var(--lo-text)" }}>Orbit</span><span style={{ color: "hsl(var(--primary))" }}>Rank</span></span>
            </div>
            <div className="lo-footer-tagline">The autonomous SEO engine that ranks your business — automatically, every single day.</div>
          </div>
          <div>
            <div className="lo-footer-col-title">Product</div>
            <div className="lo-footer-links"><a href="/#how-it-works">How It Works</a><a href="/#features">Features</a><a href="/#pricing">Pricing</a><a href="/dashboard">Dashboard</a></div>
          </div>
          <div>
            <div className="lo-footer-col-title">Resources</div>
            <div className="lo-footer-links"><a href="/blog">Blog</a><a href="#">Guides</a><a href="#">Case Studies</a><a href="#">Help Center</a></div>
          </div>
          <div>
            <div className="lo-footer-col-title">Company</div>
            <div className="lo-footer-links"><a href="#">About</a><a href="#">Contact</a><a href="#">Support</a><a href="/auth">Login</a></div>
          </div>
          <div>
            <div className="lo-footer-col-title">Legal</div>
            <div className="lo-footer-links"><a href="/privacy-policy">Privacy Policy</a><a href="/terms-of-service">Terms of Service</a><a href="/refund-policy">Refund Policy</a></div>
          </div>
        </div>
        <div className="lo-footer-bottom">
          <div className="lo-footer-copy">OrbitRank © 2026  ·  Built for scale. Designed to rank.</div>
          <div className="lo-footer-legal"><a href="/privacy-policy">Privacy</a><a href="/terms-of-service">Terms</a><a href="/refund-policy">Refunds</a></div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
